<template>
  <div>
    <div>
      <dl class="mt-6 mb-0 space-y-4 px-0">
        <div
          v-for="outage in outages"
          :key="outage.id"
          class="relative text-left border border-solid border-black rounded-lg p-6"
          :class="{ 'pr-14 cursor-pointer': isTruncated(outage.id) }"
          :role="isTruncated(outage.id) ? 'button' : undefined"
          :tabindex="isTruncated(outage.id) ? 0 : undefined"
          :aria-expanded="isTruncated(outage.id) ? isExpanded(outage.id) : undefined"
          @click="toggle(outage.id)"
          @keydown.enter.prevent="toggle(outage.id)"
          @keydown.space.prevent="toggle(outage.id)"
        >
          <dt class="flex flex-col text-left">
            <p
              :ref="(el) => setTitleRef(el as HTMLElement | null, outage.id)"
              class="text-lg leading-6 font-poppins text-black p-0 m-0"
              :class="{ 'line-clamp-3': !isExpanded(outage.id) }"
            >
              {{ outage.title }}
            </p>
            <p class="text-md leading-6 font-poppins text-black font-bold p-0 mt-2 mb-0">Network - {{ outage.network }}</p>
          </dt>
          <dd class="flex flex-col text-left font-poppins text-black leading-6 p-0 m-0 mt-1">
            <p class="p-0 m-0">{{ outage.status }}</p>
            <p v-if="outage.plannedEndAt" class="p-0 m-0 mt-1">
              {{ plannedEndLabel }} - {{ outage.plannedEndAt }}
            </p>
          </dd>
          <component
            v-if="isTruncated(outage.id)"
            :is="isExpanded(outage.id) ? ChevronDownIcon : ChevronRightIcon"
            class="absolute top-6 right-6 h-5 w-5 text-black"
            aria-hidden="true"
          />
        </div>
      </dl>
    </div>
  </div>
</template>

<script setup lang="ts">
import { OutageData } from '@/services/integration/clients/types';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/vue/outline';
import { defineProps, computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

type OutagesList = {
  outages: Array<OutageData> | null
  type: 'current' | 'scheduled'
}

const props = defineProps<OutagesList>();

const plannedEndLabel = computed(() => (
  props.type === 'scheduled' ? 'Estimated outage completion' : 'Estimated restoration date'
));

const expandedIds = ref(new Set<string>());
const truncatedIds = ref(new Set<string>());
const titleRefs = new Map<string, HTMLElement>();

const isExpanded = (id: string) => expandedIds.value.has(id);
const isTruncated = (id: string) => truncatedIds.value.has(id);

const toggle = (id: string) => {
  if (!isTruncated(id)) return;
  const next = new Set(expandedIds.value);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  expandedIds.value = next;
};

const setTitleRef = (el: HTMLElement | null, id: string) => {
  if (el) {
    titleRefs.set(id, el);
  } else {
    titleRefs.delete(id);
  }
};

// Only re-measures collapsed titles: an expanded title isn't clamped, so its
// scrollHeight/clientHeight comparison can't tell us whether it was truncated.
const recomputeTruncation = () => {
  const next = new Set<string>();
  titleRefs.forEach((el, id) => {
    if (isExpanded(id)) {
      if (truncatedIds.value.has(id)) next.add(id);
      return;
    }
    if (el.scrollHeight > el.clientHeight + 1) {
      next.add(id);
    }
  });
  truncatedIds.value = next;
};

let resizeTimeout: ReturnType<typeof setTimeout> | undefined;
const onResize = () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(recomputeTruncation, 150);
};

onMounted(() => {
  recomputeTruncation();
  window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
  clearTimeout(resizeTimeout);
  window.removeEventListener('resize', onResize);
});

watch(() => props.outages, () => nextTick(recomputeTruncation));

</script>
